import React, { useEffect, useState, useCallback } from 'react';
import {
  Upload,
  Button,
  Table,
  Alert,
  Space,
  Tag,
  Typography,
  Steps,
  Result,
  message,
  Descriptions,
  Collapse,
  theme,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  CopyOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import type { ColumnsType } from 'antd/es/table';
import { trpc } from '../../lib/trpc';
import { withDevModalFilePath } from '../common';

const { Text, Link, Paragraph } = Typography;

// 開発環境かどうかを判定
const isDevelopment = import.meta.env.DEV;

interface UserBulkInviteModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  companyId?: string;
}

interface PreviewRow {
  rowNumber: number;
  data: {
    email?: string;
    name?: string;
    role?: string;
    department?: string | null;
    position?: string | null;
  } | null;
  errors: string[];
  warnings: string[];
}

interface InviteResult {
  email: string;
  name: string;
  success: boolean;
  error?: string;
}

interface DevEmailItem {
  to: string;
  name: string;
  role: string;
  department?: string;
  position?: string;
  setupUrl: string;
  token: string;
  expiresAt: string;
}

const { Modal: DevModal } = withDevModalFilePath('client/src/components/user/UserBulkInviteModal.tsx');

const getRoleText = (role: string) => {
  switch (role) {
    case 'admin': return '管理者';
    case 'manager': return 'マネージャー';
    case 'user': return 'ユーザー';
    case 'viewer': return '閲覧者';
    default: return role;
  }
};

const getRoleColor = (role: string) => {
  switch (role) {
    case 'admin': return 'red';
    case 'manager': return 'orange';
    case 'user': return 'blue';
    case 'viewer': return 'gray';
    default: return 'default';
  }
};

export function UserBulkInviteModal({
  visible,
  onCancel,
  onSuccess,
  companyId,
}: UserBulkInviteModalProps) {
  const { token } = theme.useToken();
  const [currentStep, setCurrentStep] = useState(0);
  // csvContentはsetCsvContentで設定され、uploadBeforeプロップのonChangeで使用可能
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [csvContent, setCsvContent] = useState<string>('');
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [previewValid, setPreviewValid] = useState(false);
  const [inviteResults, setInviteResults] = useState<InviteResult[]>([]);
  const [devEmails, setDevEmails] = useState<DevEmailItem[]>([]);
  const [messageApi, messageContextHolder] = message.useMessage();

  // CSVテンプレート取得
  const templateQuery = trpc.user.bulkInviteTemplate.useQuery(undefined, {
    enabled: false,
  });

  // CSVプレビュー
  const previewMutation = trpc.user.bulkInvitePreview.useMutation({
    onSuccess: (data) => {
      setPreviewData(data.rows);
      setPreviewValid(data.valid);
      setCurrentStep(1);
    },
    onError: (error) => {
      messageApi.error(error.message || 'CSVの解析に失敗しました');
    },
  });

  // 一括招待実行
  const bulkInviteMutation = trpc.user.bulkInvite.useMutation({
    onSuccess: (data) => {
      setInviteResults(data.results);
      setCurrentStep(2);

      // 開発環境: メール情報を取得
      if (isDevelopment) {
        devEmailsQuery.refetch();
      }
    },
    onError: (error) => {
      messageApi.error(error.message || '一括招待に失敗しました');
    },
  });

  // 開発環境用: メール情報取得
  const devEmailsQuery = trpc.user.getLastDevBulkEmails.useQuery(undefined, {
    enabled: false,
  });
  useEffect(() => {
    if (devEmailsQuery.data?.emails) {
      setDevEmails(
        devEmailsQuery.data.emails.filter(
          (email): email is DevEmailItem => typeof email.to === 'string' && email.to.length > 0
        )
      );
    }
  }, [devEmailsQuery.data]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const result = await templateQuery.refetch();
      if (result.data?.csvContent) {
        const blob = new Blob([result.data.csvContent], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'user_invite_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch {
      messageApi.error('テンプレートのダウンロードに失敗しました');
    }
  }, [templateQuery, messageApi]);

  const handleFileUpload = useCallback((file: UploadFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      // BOM除去
      const cleanContent = content.replace(/^\uFEFF/, '');
      setCsvContent(cleanContent);
      previewMutation.mutate({
        csvContent: cleanContent,
        companyId,
      });
    };
    reader.readAsText(file as unknown as Blob, 'UTF-8');
    return false; // 自動アップロードを防止
  }, [previewMutation, companyId]);

  const handleExecuteInvite = useCallback(() => {
    const validRows = previewData
      .filter(row => row.errors.length === 0 && row.data)
      .map(row => ({
        email: row.data!.email,
        name: row.data!.name,
        role: row.data!.role as 'admin' | 'manager' | 'user' | 'viewer',
        department: row.data!.department || undefined,
        position: row.data!.position || undefined,
      }));

    if (validRows.length === 0) {
      messageApi.error('招待可能なユーザーがいません');
      return;
    }

    bulkInviteMutation.mutate({
      rows: validRows,
      companyId,
    });
  }, [previewData, bulkInviteMutation, companyId, messageApi]);

  const handleClose = useCallback(() => {
    setCurrentStep(0);
    setCsvContent('');
    setPreviewData([]);
    setPreviewValid(false);
    setInviteResults([]);
    setDevEmails([]);
    onCancel();
  }, [onCancel]);

  const handleComplete = useCallback(() => {
    handleClose();
    onSuccess();
  }, [handleClose, onSuccess]);

  const handleCopyUrl = useCallback((url: string) => {
    navigator.clipboard.writeText(url);
    messageApi.success('URLをコピーしました');
  }, [messageApi]);

  // プレビューテーブルのカラム
  const previewColumns: ColumnsType<PreviewRow> = [
    {
      title: '行',
      dataIndex: 'rowNumber',
      key: 'rowNumber',
      width: 60,
    },
    {
      title: 'メールアドレス',
      key: 'email',
      render: (_, record) => record.data?.email || '-',
    },
    {
      title: '氏名',
      key: 'name',
      render: (_, record) => record.data?.name || '-',
    },
    {
      title: '権限',
      key: 'role',
      width: 120,
      render: (_, record) => record.data?.role ? (
        <Tag color={getRoleColor(record.data.role)}>
          {getRoleText(record.data.role)}
        </Tag>
      ) : '-',
    },
    {
      title: '部署',
      key: 'department',
      render: (_, record) => record.data?.department || '-',
    },
    {
      title: '役職',
      key: 'position',
      render: (_, record) => record.data?.position || '-',
    },
    {
      title: 'ステータス',
      key: 'status',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.errors.length > 0 && (
            record.errors.map((err, i) => (
              <Text key={i} type="danger" style={{ fontSize: 12 }}>
                <CloseCircleOutlined /> {err}
              </Text>
            ))
          )}
          {record.warnings.length > 0 && (
            record.warnings.map((warn, i) => (
              <Text key={i} type="warning" style={{ fontSize: 12 }}>
                <WarningOutlined /> {warn}
              </Text>
            ))
          )}
          {record.errors.length === 0 && record.warnings.length === 0 && (
            <Text type="success" style={{ fontSize: 12 }}>
              <CheckCircleOutlined /> OK
            </Text>
          )}
        </Space>
      ),
    },
  ];

  // 結果テーブルのカラム
  const resultColumns: ColumnsType<InviteResult> = [
    {
      title: 'メールアドレス',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '氏名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '結果',
      key: 'result',
      render: (_, record) => record.success ? (
        <Tag color="success" icon={<CheckCircleOutlined />}>成功</Tag>
      ) : (
        <Tag color="error" icon={<CloseCircleOutlined />}>{record.error || '失敗'}</Tag>
      ),
    },
  ];

  const successCount = inviteResults.filter(r => r.success).length;
  const failedCount = inviteResults.filter(r => !r.success).length;

  return (
    <>
      {messageContextHolder}
      <DevModal
        title="CSV一括招待"
        open={visible}
        onCancel={handleClose}
        width={900}
        footer={null}
        destroyOnHidden
      >
        <Steps
          current={currentStep}
          items={[
            { title: 'CSVアップロード' },
            { title: 'プレビュー・確認' },
            { title: '完了' },
          ]}
          style={{ marginBottom: 24 }}
        />

        {/* ステップ1: CSVアップロード */}
        {currentStep === 0 && (
          <div>
            <Alert
              message="CSV一括招待について"
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                  <li>CSVファイルから複数ユーザーを一括で招待できます</li>
                  <li>各ユーザーに招待メールが送信され、メール内のURLからパスワードを設定できます</li>
                  <li>一度に招待できるのは最大100人までです</li>
                </ul>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Text strong>1. テンプレートをダウンロード</Text>
                <div style={{ marginTop: 8 }}>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadTemplate}
                    loading={templateQuery.isFetching}
                  >
                    CSVテンプレートをダウンロード
                  </Button>
                </div>
              </div>

              <div>
                <Text strong>2. CSVファイルをアップロード</Text>
                <div style={{ marginTop: 8 }}>
                  <Upload
                    accept=".csv"
                    maxCount={1}
                    beforeUpload={handleFileUpload}
                    showUploadList={false}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={previewMutation.isPending}
                    >
                      CSVファイルを選択
                    </Button>
                  </Upload>
                </div>
              </div>
            </Space>
          </div>
        )}

        {/* ステップ2: プレビュー・確認 */}
        {currentStep === 1 && (
          <div>
            <Space style={{ marginBottom: 16 }}>
              <Text>
                全 {previewData.length} 件中、
                <Text type="success" strong> {previewData.filter(r => r.errors.length === 0).length} 件</Text> が招待可能、
                <Text type="danger" strong> {previewData.filter(r => r.errors.length > 0).length} 件</Text> がエラー
              </Text>
            </Space>

            {!previewValid && (
              <Alert
                message="エラーがあります"
                description="エラーのある行は招待されません。CSVを修正して再アップロードするか、エラーのない行のみ招待を続行してください。"
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Table
              columns={previewColumns}
              dataSource={previewData}
              rowKey="rowNumber"
              size="small"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 800 }}
              rowClassName={(record) => record.errors.length > 0 ? 'ant-table-row-error' : ''}
            />

            <Space style={{ marginTop: 16 }}>
              <Button onClick={() => setCurrentStep(0)}>
                戻る
              </Button>
              <Button
                type="primary"
                onClick={handleExecuteInvite}
                loading={bulkInviteMutation.isPending}
                disabled={previewData.filter(r => r.errors.length === 0).length === 0}
              >
                {previewData.filter(r => r.errors.length === 0).length} 件を招待
              </Button>
            </Space>
          </div>
        )}

        {/* ステップ3: 完了 */}
        {currentStep === 2 && (
          <div>
            <Result
              status={failedCount === 0 ? 'success' : 'warning'}
              title={failedCount === 0 ? '一括招待が完了しました' : '一括招待が完了しました（一部エラー）'}
              subTitle={`成功: ${successCount} 件 / 失敗: ${failedCount} 件`}
            />

            <Table
              columns={resultColumns}
              dataSource={inviteResults}
              rowKey="email"
              size="small"
              pagination={{ pageSize: 10 }}
              style={{ marginBottom: 16 }}
            />

            {/* 開発環境用: 招待URLリスト */}
            {isDevelopment && devEmails.length > 0 && (
              <Collapse
                items={[
                  {
                    key: 'dev-urls',
                    label: (
                      <Space>
                        <Tag color="orange">開発環境</Tag>
                        <Text>招待URLリスト（{devEmails.length}件）</Text>
                      </Space>
                    ),
                    children: (
                      <div style={{ maxHeight: 400, overflow: 'auto' }}>
                        {devEmails.map((email, index) => (
                          <div
                            key={index}
                            style={{
                              padding: 12,
                              marginBottom: 8,
                              background: token.colorFillQuaternary,
                              borderRadius: 4,
                            }}
                          >
                            <Descriptions size="small" column={1}>
                              <Descriptions.Item label="宛先">
                                {email.name} ({email.to})
                              </Descriptions.Item>
                              <Descriptions.Item label="権限">
                                <Tag color={getRoleColor(email.role)}>
                                  {getRoleText(email.role)}
                                </Tag>
                              </Descriptions.Item>
                              {email.department && (
                                <Descriptions.Item label="部署">
                                  {email.department}
                                </Descriptions.Item>
                              )}
                              {email.position && (
                                <Descriptions.Item label="役職">
                                  {email.position}
                                </Descriptions.Item>
                              )}
                              <Descriptions.Item label="有効期限">
                                {new Date(email.expiresAt).toLocaleString('ja-JP')}
                              </Descriptions.Item>
                            </Descriptions>
                            <div style={{ marginTop: 8 }}>
                              <Text strong>セットアップURL:</Text>
                              <Paragraph
                                style={{
                                  background: token.colorBgContainer,
                                  padding: '4px 8px',
                                  borderRadius: 4,
                                  marginTop: 4,
                                  marginBottom: 4,
                                  wordBreak: 'break-all',
                                  fontSize: 12,
                                }}
                              >
                                <Link href={email.setupUrl} target="_blank">
                                  {email.setupUrl}
                                </Link>
                              </Paragraph>
                              <Space size="small">
                                <Button
                                  size="small"
                                  icon={<CopyOutlined />}
                                  onClick={() => handleCopyUrl(email.setupUrl)}
                                >
                                  コピー
                                </Button>
                                <Button
                                  size="small"
                                  type="primary"
                                  icon={<LinkOutlined />}
                                  onClick={() => window.open(email.setupUrl, '_blank')}
                                >
                                  開く
                                </Button>
                              </Space>
                            </div>
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
                defaultActiveKey={['dev-urls']}
              />
            )}

            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Button type="primary" onClick={handleComplete}>
                閉じる
              </Button>
            </div>
          </div>
        )}
      </DevModal>

      <style>{`
        .ant-table-row-error {
          background-color: ${token.colorErrorBg} !important;
        }
      `}</style>
    </>
  );
}

export default UserBulkInviteModal;
